import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { CreateField, CreateModal } from '@/card/CreateModal'
import { RecordPicker } from '@/card/RecordPicker'
import { ApiError } from '@/lib/api'
import {
  createMeeting,
  createTask,
  fetchPeople,
  updateMeeting,
  updateTask,
  type ActivityEntityType,
  type CalendarEvent,
  type EventRef,
  type People,
} from './api'
import { fromLocalInput, toLocalInput } from './dates'
import '@/styles/calendar.css'

/**
 * Формы задачи и встречи (#110, #112) — одни для календаря, «Моих задач» и карточки.
 *
 * Запись, к которой относится задача, передаёт тот, кто открыл форму: карточка сделки —
 * свою сделку, календарь и «Мои задачи» — никакую. Задача без записи законна: «отчёт
 * руководителю» не относится ни к одной сделке.
 *
 * «Напомнить за» по умолчанию — **значение из системных настроек**: форма его не знает и не
 * подставляет своё, а не присылает поле вовсе. Своё число на клиенте разошлось бы с
 * настройкой администратора при первой её правке.
 */

export interface ActivityTarget {
  type: ActivityEntityType
  id: string
  label?: string
}

/** Варианты «напомнить за»; `''` — по настройкам, `'none'` — не напоминать. */
const REMIND_OPTIONS = ['', 'none', '0', '5', '10', '15', '30', '60', '120', '1440'] as const

function remindValue(value: string): number | null | undefined {
  if (value === '') return undefined
  if (value === 'none') return -1
  return Number(value)
}

function remindOption(minutes: number | null | undefined, editing: boolean): string {
  if (minutes == null) return editing ? 'none' : ''
  const option = String(minutes)
  return (REMIND_OPTIONS as readonly string[]).includes(option) ? option : ''
}

export function usePeople() {
  return useQuery({
    queryKey: ['calendar-people'],
    queryFn: ({ signal }) => fetchPeople(signal),
    retry: false,
    staleTime: 60_000,
  })
}

function RemindSelect({ id, value, onChange }: { id: string; value: string; onChange: (value: string) => void }) {
  const { t } = useApp()
  return (
    <select id={id} className="input" value={value} onChange={(event) => onChange(event.target.value)}>
      {REMIND_OPTIONS.map((option) => (
        <option key={option} value={option}>
          {option === '' ? t('cal.remind.default') : option === 'none' ? t('cal.remind.none') : t(`cal.remind.${option}`)}
        </option>
      ))}
    </select>
  )
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

// ------------------------------------------------------------------ задача

export function TaskFormModal({
  target,
  task,
  defaultDue,
  onClose,
  onSaved,
}: {
  /** Запись, к которой ставится задача; нет — задача без записи. */
  target?: ActivityTarget | null
  /** Правка существующей задачи. */
  task?: CalendarEvent | null
  defaultDue?: Date | null
  onClose: () => void
  onSaved?: (saved: EventRef) => void
}) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const people = usePeople()
  const editing = Boolean(task)
  const [subject, setSubject] = useState(task?.subject ?? '')
  const [body, setBody] = useState(task?.body ?? '')
  const [due, setDue] = useState(() => {
    const initial = task?.dueAt ?? task?.start ?? null
    if (initial) return toLocalInput(new Date(initial))
    return defaultDue ? toLocalInput(defaultDue) : ''
  })
  const [assignee, setAssignee] = useState(user?.employeeId ?? '')
  const [remind, setRemind] = useState(remindOption(task?.remindBeforeMinutes, editing))
  const [recurrence, setRecurrence] = useState(task?.recurrence ?? 'none')
  const [until, setUntil] = useState(task?.recurrenceUntil ?? '')

  const save = useMutation({
    mutationFn: () => {
      const dueAt = fromLocalInput(due)
      const input = {
        subject: subject.trim(),
        body: body.trim() === '' ? null : body.trim(),
        dueAt: dueAt ? dueAt.toISOString() : null,
        remindBeforeMinutes: remindValue(remind),
        recurrence: recurrence === 'none' ? null : recurrence,
        recurrenceUntil: recurrence === 'none' || until === '' ? null : until,
      }
      return task
        ? updateTask(task.id, input)
        : createTask({
            ...input,
            entityType: target?.type ?? null,
            entityId: target?.id ?? null,
            assigneeId: assignee === '' ? null : assignee,
          })
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['calendar'] })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
      if (target) {
        void queryClient.invalidateQueries({ queryKey: ['timeline', target.type, target.id] })
        void queryClient.invalidateQueries({ queryKey: ['activities', target.type, target.id] })
      }
      onSaved?.(saved)
      onClose()
    },
  })

  const blockedReason = subject.trim() === ''
    ? t('cal.form.needSubject')
    : recurrence !== 'none' && fromLocalInput(due) == null
      ? t('cal.form.needDueForRepeat')
      : !target && !editing && assignee === ''
        ? t('cal.form.needAssignee')
        : null

  const assignable = assignableOf(people.data, user?.employeeId ?? null)

  return (
    <CreateModal
      overline={target ? `${t('cal.form.task.overline')} · ${target.label ?? t(`myday.entity.${target.type}`)}` : t('cal.form.task.overline')}
      title={editing ? t('cal.form.task.edit') : t('cal.form.task.new')}
      error={save.isError ? messageOf(save.error, t('app.error')) : null}
      blockedReason={blockedReason}
      pending={save.isPending}
      submitLabel={editing ? t('cal.form.save') : t('cal.form.task.create')}
      onCancel={onClose}
      onSubmit={() => save.mutate()}
      more={{
        content: (
          <CreateField id="task-body" label={t('cal.form.body')} span>
            <textarea id="task-body" className="input" value={body} onChange={(event) => setBody(event.target.value)} />
          </CreateField>
        ),
      }}
    >
      <div className="form-grid">
        <CreateField id="task-subject" label={t('cal.form.subject')} required span>
          <input id="task-subject" className="input" value={subject} onChange={(event) => setSubject(event.target.value)} />
        </CreateField>
        <CreateField id="task-due" label={t('cal.form.due')}>
          <input id="task-due" className="input" type="datetime-local" value={due} onChange={(event) => setDue(event.target.value)} />
        </CreateField>
        <CreateField id="task-remind" label={t('cal.form.remind')}>
          <RemindSelect id="task-remind" value={remind} onChange={setRemind} />
        </CreateField>
        {!editing && (
          <CreateField id="task-assignee" label={t('cal.form.assignee')} span
                       help={assignable.length <= 1 ? t('cal.form.assignee.ownHelp') : undefined}>
            <select id="task-assignee" className="input" value={assignee} onChange={(event) => setAssignee(event.target.value)}>
              {assignee === '' && <option value="">{t('cal.form.assignee.none')}</option>}
              {assignable.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.id === user?.employeeId ? t('cal.form.me', { name: person.name ?? '' }) : person.name ?? person.id}
                </option>
              ))}
            </select>
          </CreateField>
        )}
        <CreateField id="task-repeat" label={t('cal.form.repeat')}>
          <select id="task-repeat" className="input" value={recurrence} onChange={(event) => setRecurrence(event.target.value)}>
            {['none', 'daily', 'weekly', 'monthly'].map((code) => (
              <option key={code} value={code}>{t(`cal.repeat.${code}`)}</option>
            ))}
          </select>
        </CreateField>
        {recurrence !== 'none' && (
          <CreateField id="task-until" label={t('cal.form.until')} help={t('cal.form.until.help')}>
            <input id="task-until" className="input" type="date" value={until} onChange={(event) => setUntil(event.target.value)} />
          </CreateField>
        )}
      </div>
    </CreateModal>
  )
}

/**
 * Кому можно поставить задачу — те, чей календарь открыт смотрящему.
 *
 * Это подсказка, а не правило: назначение проверяет сервер (`ResponsibleGuard`), и
 * область создания у ролей совпадает с областью чтения. Себя — всегда первым.
 */
function assignableOf(people: People | undefined, me: string | null) {
  const list = (people?.people ?? []).filter((person) => person.viewable)
  list.sort((a, b) => (a.id === me ? -1 : b.id === me ? 1 : 0))
  if (me && !list.some((person) => person.id === me)) list.unshift({ id: me, viewable: true })
  return list
}

// ------------------------------------------------------------------ встреча

export function MeetingFormModal({
  target,
  meeting,
  defaultStart,
  onClose,
  onSaved,
}: {
  target?: ActivityTarget | null
  meeting?: CalendarEvent | null
  defaultStart?: Date | null
  onClose: () => void
  onSaved?: (saved: EventRef) => void
}) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const people = usePeople()
  const editing = Boolean(meeting)
  const initialStart = meeting ? new Date(meeting.start) : defaultStart ?? null
  const initialEnd = meeting?.endsAt
    ? new Date(meeting.endsAt)
    : initialStart ? new Date(initialStart.getTime() + 60 * 60_000) : null
  const [subject, setSubject] = useState(meeting?.subject ?? '')
  const [body, setBody] = useState(meeting?.body ?? '')
  const [start, setStart] = useState(initialStart ? toLocalInput(initialStart) : '')
  const [end, setEnd] = useState(initialEnd ? toLocalInput(initialEnd) : '')
  const [location, setLocation] = useState(meeting?.location ?? '')
  const [remind, setRemind] = useState(remindOption(meeting?.remindBeforeMinutes, editing))
  const [employees, setEmployees] = useState<string[]>(
    () => (meeting?.participants ?? []).flatMap((p) => (p.employeeId ? [p.employeeId] : [])),
  )
  const [contacts, setContacts] = useState<{ id: string; label: string }[]>(
    () => (meeting?.participants ?? []).flatMap((p) =>
      p.contactId ? [{ id: p.contactId, label: p.name ?? t('cal.contact.hidden') }] : []),
  )
  const [personFilter, setPersonFilter] = useState('')

  const save = useMutation({
    mutationFn: () => {
      const startsAt = fromLocalInput(start)
      const endsAt = fromLocalInput(end)
      const input = {
        subject: subject.trim(),
        body: body.trim() === '' ? null : body.trim(),
        startsAt: startsAt ? startsAt.toISOString() : '',
        endsAt: endsAt ? endsAt.toISOString() : null,
        location: location.trim() === '' ? null : location.trim(),
        remindBeforeMinutes: remindValue(remind),
        employeeIds: employees,
        contactIds: contacts.map((contact) => contact.id),
      }
      return meeting
        ? updateMeeting(meeting.id, input)
        : createMeeting({ ...input, entityType: target?.type ?? null, entityId: target?.id ?? null })
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['calendar'] })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
      if (target) {
        void queryClient.invalidateQueries({ queryKey: ['timeline', target.type, target.id] })
        void queryClient.invalidateQueries({ queryKey: ['activities', target.type, target.id] })
      }
      onSaved?.(saved)
      onClose()
    },
  })

  const startsAt = fromLocalInput(start)
  const endsAt = fromLocalInput(end)
  const blockedReason = subject.trim() === ''
    ? t('cal.form.needSubject')
    : startsAt == null
      ? t('cal.form.needStart')
      : endsAt != null && endsAt.getTime() <= startsAt.getTime()
        ? t('cal.form.endBeforeStart')
        : null

  const needle = personFilter.trim().toLowerCase()
  const candidates = (people.data?.people ?? [])
    .filter((person) => person.id !== (meeting?.assigneeEmployeeId ?? user?.employeeId))
    .filter((person) => !needle || (person.name ?? '').toLowerCase().includes(needle)
      || (person.departmentName ?? '').toLowerCase().includes(needle))
  const toggle = (id: string) =>
    setEmployees((was) => (was.includes(id) ? was.filter((other) => other !== id) : [...was, id]))

  return (
    <CreateModal
      overline={target ? `${t('cal.form.meeting.overline')} · ${target.label ?? t(`myday.entity.${target.type}`)}` : t('cal.form.meeting.overline')}
      title={editing ? t('cal.form.meeting.edit') : t('cal.form.meeting.new')}
      error={save.isError ? messageOf(save.error, t('app.error')) : null}
      blockedReason={blockedReason}
      pending={save.isPending}
      submitLabel={editing ? t('cal.form.save') : t('cal.form.meeting.create')}
      onCancel={onClose}
      onSubmit={() => save.mutate()}
      more={{
        content: (
          <CreateField id="meeting-body" label={t('cal.form.agenda')} span>
            <textarea id="meeting-body" className="input" value={body} onChange={(event) => setBody(event.target.value)} />
          </CreateField>
        ),
      }}
    >
      <div className="form-grid">
        <CreateField id="meeting-subject" label={t('cal.form.subject')} required span>
          <input id="meeting-subject" className="input" value={subject} onChange={(event) => setSubject(event.target.value)} />
        </CreateField>
        <CreateField id="meeting-start" label={t('cal.form.start')} required>
          <input id="meeting-start" className="input" type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} />
        </CreateField>
        <CreateField id="meeting-end" label={t('cal.form.end')}>
          <input id="meeting-end" className="input" type="datetime-local" value={end} onChange={(event) => setEnd(event.target.value)} />
        </CreateField>
        <CreateField id="meeting-location" label={t('cal.form.location')} help={t('cal.form.location.help')}>
          <input id="meeting-location" className="input" value={location} onChange={(event) => setLocation(event.target.value)} />
        </CreateField>
        <CreateField id="meeting-remind" label={t('cal.form.remind')}>
          <RemindSelect id="meeting-remind" value={remind} onChange={setRemind} />
        </CreateField>

        <CreateField id="meeting-people-filter" label={t('cal.form.employees')} span
                     help={t('cal.form.employees.help', { count: employees.length })}>
          <input
            id="meeting-people-filter"
            className="input input-sm"
            placeholder={t('cal.form.employees.filter')}
            value={personFilter}
            onChange={(event) => setPersonFilter(event.target.value)}
          />
          {people.isError ? (
            <span className="t-sm muted" role="alert">{t('cal.people.error')}</span>
          ) : (
            <ul className="cal-pick" aria-label={t('cal.form.employees')}>
              {candidates.map((person) => (
                <li key={person.id}>
                  <label className="row">
                    <input type="checkbox" checked={employees.includes(person.id)} onChange={() => toggle(person.id)} />
                    <span className="ellipsis">{person.name ?? person.id}</span>
                    {person.departmentName && <span className="t-sm muted ellipsis">{person.departmentName}</span>}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </CreateField>

        <CreateField id="meeting-contact" label={t('cal.form.contacts')} span>
          {contacts.length > 0 && (
            <div className="row-wrap">
              {contacts.map((contact) => (
                <span key={contact.id} className="badge">
                  {contact.label}
                  <button
                    type="button"
                    className="cal-chip-x"
                    aria-label={t('cal.form.contacts.remove', { name: contact.label })}
                    onClick={() => setContacts((was) => was.filter((other) => other.id !== contact.id))}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <RecordPicker
            id="meeting-contact"
            entity="sales.contact"
            searchColumn="last_name"
            extraColumn="first_name"
            value={null}
            onChange={(picked) => {
              if (picked && !contacts.some((contact) => contact.id === picked.id)) {
                setContacts((was) => [...was, picked])
              }
            }}
          />
        </CreateField>
      </div>
    </CreateModal>
  )
}
